import React, { useMemo, useState } from "react";
import API from "../api";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";

// NOA Value Pod Platform - Multi-Step Onboarding Wizard
// Single-file React component using Tailwind CSS.

const USER_TYPES = [
  "Startup",
  "Corporate",
  "Investor",
  "Advisor",
  "Government",
  "NGO",
  "Research Institution",
  "Academic Institutions",
  "Financial Institutions",
];

const SECTORS = [
  "Green Hydrogen",
  "Green Buildings",
  "Sustainable Agriculture",
  "Offshore Wind",
  "Green Mobility",
  "Battery Storage (BESS)",
  "Solar and Onshore Wind",
  "AI Energy Transition",
  "Critical Minerals",
];

// POD definitions (offerings, needs, funding requirements) based on requested value pods
const PODS = {
  "GREEN HYDROGEN VALUE POD": {
    offerings: [
      "Electrolyzer technology",
      "EPC for hydrogen plants",
      "Renewable power integration",
      "Hydrogen storage systems",
      "Compression & transport systems",
      "Carbon accounting & certification",
      "Offtake agreements / industrial buyers",
    ],
    needs: [
      "Land acquisition / industrial zone access",
      "Renewable energy sourcing (solar/wind)",
      "Water sourcing & treatment systems",
      "Grid connectivity approvals",
      "Electrolyzers and balance of plant",
      "Industrial offtakers (steel, fertilizer, mobility)",
      "Project financing (debt + promoter equity)",
    ],
    funding: [
      "Project Development Capital",
      "Working Capital",
      "Equity for promoter contribution",
      "Long-term infrastructure debt",
      "Export credit financing",
    ],
  },
  "GREEN VEHICLE / EV VALUE POD": {
    offerings: [
      "EV manufacturing technology",
      "Charging infrastructure",
      "Battery solutions",
      "Fleet electrification services",
      "Telematics / fleet software",
      "Carbon reduction analytics",
      "Leasing / financing products",
    ],
    needs: [
      "Vehicle assembly partners",
      "Battery suppliers",
      "Charging infrastructure locations",
      "Fleet operators / anchor customers",
      "Government subsidies & permits",
      "Last-mile logistics partners",
      "Equity & debt financing",
    ],
    funding: [
      "Capex financing for manufacturing",
      "Vehicle leasing finance",
      "Venture / growth equity",
      "Working capital debt",
      "Fleet financing",
    ],
  },
  "SUSTAINABLE AGRICULTURE VALUE POD": {
    offerings: [
      "Precision farming technology",
      "Agri-IoT sensors",
      "Climate-smart irrigation",
      "Organic fertilizers / bio-inputs",
      "Controlled environment farming",
      "Carbon farming certification",
      "Supply chain & export linkage",
    ],
    needs: [
      "Agricultural land access",
      "Water systems",
      "Cold storage logistics",
      "Agri-financing partners",
      "Distribution networks",
      "Carbon credit monetization",
      "Equity for scale-up",
    ],
    funding: [
      "Farm infrastructure debt",
      "Growth equity",
      "Carbon finance",
      "Working capital",
      "Trade finance",
    ],
  },
  "SOLAR MANUFACTURING VALUE POD": {
    offerings: [
      "Solar modules",
      "Solar cells manufacturing",
      "Inverters",
      "Transformers",
      "Mounting structures",
      "Engineering services",
      "Warranty & O&M support",
    ],
    needs: [
      "Industrial land",
      "Factory setup EPC",
      "Raw materials supply chain",
      "Technology licensing",
      "Anchor customers / distributors",
      "Export channels",
      "Manufacturing finance",
    ],
    funding: [
      "Project finance debt",
      "Equipment financing",
      "Working capital lines",
      "Export financing",
      "Strategic equity",
    ],
  },
  "SOLAR & WIND IPP VALUE POD": {
    offerings: [
      "Project development expertise",
      "EPC execution",
      "O&M services",
      "PPA structuring",
      "Energy trading solutions",
      "Asset monitoring systems",
      "Carbon accounting",
    ],
    needs: [
      "Land access",
      "Transmission right of way (ROW)",
      "Grid connectivity agreement",
      "Substation / transformers",
      "Inverters & BOP systems",
      "Liaison & approvals",
      "Debt + equity financing",
    ],
    funding: [
      "Development capital",
      "Construction debt",
      "Equity contribution",
      "Refinancing capital",
      "Mezzanine finance",
    ],
  },
  "AI IN ENERGY TRANSITION VALUE POD": {
    offerings: [
      "Energy forecasting AI",
      "Grid optimization software",
      "Energy trading algorithms",
      "Demand response systems",
      "Carbon analytics",
      "Asset predictive maintenance",
      "Digital twins",
    ],
    needs: [
      "Utility partnerships",
      "Data access",
      "Cloud infrastructure",
      "Pilot customers",
      "Integration partners",
      "Enterprise sales channels",
      "Venture/growth capital",
    ],
    funding: [
      "Seed/VC funding",
      "SaaS scaling capital",
      "R&D funding",
      "Enterprise deployment capital",
      "Working capital",
    ],
  },
  "GREEN BUILDINGS VALUE POD": {
    offerings: [
      "Smart building systems",
      "HVAC optimization",
      "Green materials",
      "Energy efficiency audits",
      "Net-zero consulting",
      "Smart metering",
      "LEED/ESG certification",
    ],
    needs: [
      "Real estate developers",
      "EPC contractors",
      "Smart infra integration",
      "Financing for retrofits",
      "Carbon reporting systems",
      "Facility management partners",
      "Project funding",
    ],
    funding: [
      "Retrofit finance",
      "Green bonds",
      "Energy performance financing",
      "Debt + promoter equity",
      "ESG-linked capital",
    ],
  },
  "ENERGY STORAGE / BATTERY VALUE POD": {
    offerings: [
      "Battery systems",
      "BESS EPC",
      "EMS software",
      "Grid balancing services",
      "Recycling solutions",
      "Thermal storage tech",
      "Warranty/O&M",
    ],
    needs: [
      "Grid interconnection",
      "Utility offtakers",
      "Land/site access",
      "OEM partnerships",
      "Power trading linkage",
      "Safety certifications",
      "Project financing",
    ],
    funding: [
      "Infrastructure debt",
      "Growth equity",
      "Equipment leasing",
      "Project finance",
    ],
  },
};

function sectorToPods(sector) {
  if (!sector) return [];
  const s = sector.toLowerCase();
  const pods = [];
  if (s.includes("hydrogen")) pods.push("GREEN HYDROGEN VALUE POD");
  if (s.includes("mobility") || s.includes("green mobility") || s.includes("vehicle"))
    pods.push("GREEN VEHICLE / EV VALUE POD");
  if (s.includes("agriculture")) pods.push("SUSTAINABLE AGRICULTURE VALUE POD");
  if (s.includes("solar") && !s.includes("wind")) pods.push("SOLAR MANUFACTURING VALUE POD");
  if (s.includes("solar") || s.includes("wind") || s.includes("offshore")) pods.push("SOLAR & WIND IPP VALUE POD");
  if (s.includes("ai")) pods.push("AI IN ENERGY TRANSITION VALUE POD");
  if (s.includes("building")) pods.push("GREEN BUILDINGS VALUE POD");
  if (s.includes("battery") || s.includes("bess") || s.includes("storage")) pods.push("ENERGY STORAGE / BATTERY VALUE POD");
  return pods.filter((v, i, a) => a.indexOf(v) === i);
}

function getPodOptionsFromSectors(sectors, type) {
  if (!sectors || !sectors.length) return [];
  const items = sectors.flatMap((sec) => {
    const pods = sectorToPods(sec);
    return pods.flatMap((p) => PODS[p]?.[type] || []);
  });
  return items.filter((it, i, a) => it && a.indexOf(it) === i);
}

const VALUE_CHAIN_POSITIONS = [
  "Upstream",
  "Midstream",
  "Downstream",
  "Enabler",
];

const SECTOR_DETAILS = {
  "Green Hydrogen": {
    upstream: [
      "IPPs (solar/wind)",
      "Electrolyzer OEMs",
      "Water treatment",
      "H₂ storage/compression",
    ],
    midstream: [
      "Hydrogen EPC",
      "Pipelines & logistics",
      "Port terminals",
      "Grid/H₂ blending infra",
    ],
    downstream: [
      "Industrial offtakers (steel, cement, ammonia)",
      "Mobility (H₂ trucks/buses)",
      "Power plants",
    ],
    enablers: [
      "Finance (infrastructure, green bonds)",
      "EPC & project consultancy",
      "AI optimization",
      "MRV/certification platforms",
    ],
  },
  "Green Buildings": {
    upstream: [
      "Low-carbon materials",
      "HVAC & control systems",
      "Sensors/meters providers",
    ],
    midstream: [
      "ESCOs",
      "Retrofit contractors",
      "BIM/digital twin",
      "Architects & design firms",
    ],
    downstream: [
      "Developers / real-estate owners",
      "FM companies",
      "Hotels/malls/offices",
    ],
    enablers: [
      "Certification bodies (LEED/BREEAM)",
      "Green finance",
      "AI energy management",
    ],
  },
  "Sustainable Agriculture": {
    upstream: [
      "Seeds/biofertilizers",
      "Precision irrigation",
      "Machinery/drones",
      "Satellite & soil data",
    ],
    midstream: [
      "FPOs / co-ops",
      "Aggregators",
      "Processing units",
      "Cold chain logistics",
    ],
    downstream: [
      "Retail chains",
      "Food brands",
      "Exporters",
      "Institutional buyers",
    ],
    enablers: [
      "MRV for soil carbon",
      "Agri-fintech",
      "Organic/regenerative certification",
    ],
  },
  "Offshore Wind": {
    upstream: [
      "Turbine OEMs",
      "Composite blade makers",
      "Floating platform tech",
      "Seabed mapping & geospatial",
    ],
    midstream: [
      "EPC offshore wind",
      "Marine logistics (install vessels)",
      "Transmission infrastructure",
      "Subsea cables",
      "O&M service providers",
    ],
    downstream: [
      "Utilities & IPPs",
      "Hydrogen developers (Power-to-X)",
      "Industrial loads",
      "Grid operators",
    ],
    enablers: [
      "Finance and risk capital",
      "Permitting/regulatory advisors",
      "AI forecasting / weather models",
      "ESIA & biodiversity assessment",
    ],
  },
  "Green Mobility": {
    upstream: [
      "EV OEMs",
      "EV drivetrain components",
      "Fuel cell technology",
      "Battery materials",
    ],
    midstream: [
      "Charging infra developers",
      "Fleet electrification integrators",
      "Hydrogen refueling networks",
      "Mobility-as-a-service tools",
    ],
    downstream: [
      "Transit authorities",
      "Logistics operators",
      "Ride-hailing companies",
      "Airports/public transport",
    ],
    enablers: [
      "Green financing/leasing",
      "Insurance underwriting",
      "MRV for mobility emissions",
      "Compliance & certification",
    ],
  },
  "Battery Storage (BESS)": {
    upstream: [
      "Battery manufacturers (LFP, NMC, Na-ion)",
      "Second-life battery platforms",
      "Inverters/converters",
      "Battery materials supply",
    ],
    midstream: [
      "BESS EPC",
      "Hybrid (RE + storage) integrators",
      "Microgrid developers",
      "O&M service providers",
    ],
    downstream: [
      "Utilities",
      "C&I customers",
      "Data centers",
      "IPPs seeking firming capacity",
    ],
    enablers: [
      "Storage financiers",
      "Regulatory compliance",
      "MRV for storage efficiency",
      "Digital twins & predictive analytics",
    ],
  },
  "Critical Minerals": {
    upstream: [
      "Mining companies",
      "Exploration & geospatial",
      "Mineral processing",
      "Raw material sourcing",
    ],
    midstream: [
      "Refiners",
      "Battery material manufacturers",
      "Processing & fabrication",
      "Supply chain logistics",
    ],
    downstream: [
      "EV manufacturers",
      "Renewable energy OEMs",
      "Electronics manufacturers",
      "End-use industries",
    ],
    enablers: [
      "Supply chain finance",
      "Recycling & circular economy",
      "Sustainability certification",
      "MRV & traceability platforms",
    ],
  },
};

const COUNTRIES = [
  "Afghanistan",
  "Albania",
  "Algeria",
  "Andorra",
  "Angola",
  "Argentina",
  "Armenia",
  "Australia",
  "Austria",
  "Azerbaijan",
  "Bahamas",
  "Bahrain",
  "Bangladesh",
  "Barbados",
  "Belarus",
  "Belgium",
  "Belize",
  "Benin",
  "Bhutan",
  "Bolivia",
  "Bosnia and Herzegovina",
  "Botswana",
  "Brazil",
  "Brunei",
  "Bulgaria",
  "Burkina Faso",
  "Burundi",
  "Cambodia",
  "Cameroon",
  "Canada",
  "Cape Verde",
  "Central African Republic",
  "Chad",
  "Chile",
  "China",
  "Colombia",
  "Comoros",
  "Congo",
  "Costa Rica",
  "Croatia",
  "Cuba",
  "Cyprus",
  "Czech Republic",
  "Czechia",
  "Denmark",
  "Djibouti",
  "Dominica",
  "Dominican Republic",
  "East Timor",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Equatorial Guinea",
  "Eritrea",
  "Estonia",
  "Eswatini",
  "Ethiopia",
  "Fiji",
  "Finland",
  "France",
  "Gabon",
  "Gambia",
  "Georgia",
  "Germany",
  "Ghana",
  "Greece",
  "Grenada",
  "Guatemala",
  "Guinea",
  "Guinea-Bissau",
  "Guyana",
  "Haiti",
  "Honduras",
  "Hong Kong",
  "Hungary",
  "Iceland",
  "India",
  "Indonesia",
  "Iran",
  "Iraq",
  "Ireland",
  "Israel",
  "Italy",
  "Ivory Coast",
  "Jamaica",
  "Japan",
  "Jordan",
  "Kazakhstan",
  "Kenya",
  "Kiribati",
  "Kosovo",
  "Kuwait",
  "Kyrgyzstan",
  "Laos",
  "Latvia",
  "Lebanon",
  "Lesotho",
  "Liberia",
  "Libya",
  "Liechtenstein",
  "Lithuania",
  "Luxembourg",
  "Macao",
  "Madagascar",
  "Malawi",
  "Malaysia",
  "Maldives",
  "Mali",
  "Malta",
  "Marshall Islands",
  "Mauritania",
  "Mauritius",
  "Mexico",
  "Micronesia",
  "Moldova",
  "Monaco",
  "Mongolia",
  "Montenegro",
  "Morocco",
  "Mozambique",
  "Myanmar",
  "Namibia",
  "Nauru",
  "Nepal",
  "Netherlands",
  "New Zealand",
  "Nicaragua",
  "Niger",
  "Nigeria",
  "North Korea",
  "North Macedonia",
  "Norway",
  "Oman",
  "Pakistan",
  "Palau",
  "Palestine",
  "Panama",
  "Papua New Guinea",
  "Paraguay",
  "Peru",
  "Philippines",
  "Poland",
  "Portugal",
  "Qatar",
  "Romania",
  "Russia",
  "Rwanda",
  "Saint Kitts and Nevis",
  "Saint Lucia",
  "Saint Vincent and the Grenadines",
  "Samoa",
  "San Marino",
  "Sao Tome and Principe",
  "Saudi Arabia",
  "Senegal",
  "Serbia",
  "Seychelles",
  "Sierra Leone",
  "Singapore",
  "Slovakia",
  "Slovenia",
  "Solomon Islands",
  "Somalia",
  "South Africa",
  "South Korea",
  "South Sudan",
  "Spain",
  "Sri Lanka",
  "Sudan",
  "Suriname",
  "Sweden",
  "Switzerland",
  "Syria",
  "Taiwan",
  "Tajikistan",
  "Tanzania",
  "Thailand",
  "Timor-Leste",
  "Togo",
  "Tonga",
  "Trinidad and Tobago",
  "Tunisia",
  "Turkey",
  "Turkmenistan",
  "Tuvalu",
  "Uganda",
  "Ukraine",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "Uruguay",
  "Uzbekistan",
  "Vanuatu",
  "Vatican City",
  "Venezuela",
  "Vietnam",
  "Yemen",
  "Zambia",
  "Zimbabwe",
];

const FUNDING_STAGES = [
  "Bootstrapped",
  "Seed",
  "Series A",
  "Series B+",
  "Growth",
  "Project Finance Ready",
];

const OPERATION_SINCE = [
  "Less than 1 year",
  "1-5 years",
  "5-10 years",
  "10-20 years",
  "20+ years",
];

const ORGANIZATION_SIZES = [
  "1-10",
  "11-50",
  "51-200",
  "201-1000",
  "1000+",
];

const EBITDA_OPTIONS = [
  "Yes",
  "No",
  "Not Sure",
];

const NOA_JOIN_REASONS = [
  "Global expansion through customer orders",
  "Strategic partnership",
  "funding & IPO exit",
  "Only Funding",
  "All of the above",
];

const PROJECT_SIZE_OPTIONS = [
  "$ 25000- 100000",
  "$ 100000- 500000",
  "$ 500000 -1 million",
  "Between $1- 3 Million",
  "Above > $3 million",
];

const PROJECT_FINISHED_OPTIONS = [
  "1yr",
  "6month",
  "3 month",
];

const STEPS = [
  "Personal",
  "Organization",
  "Value Chain Profile",
  "Offers & Needs",
  "Past Project Execution Credentials",
  "Review and Register",
];

const initialForm = {
  // Personal
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
  jobTitle: "",

  // Organization
  organizationName: "",
  website: "",
  country: "India",
  foundedYear: "",
  organizationSize: "",

  // Value Chain Profile
  userType: "",
  sectors: [],
  valueChainPosition: "",
  sectorSpecializations: [],

  // Strategic
  offers: [],
  needs: [],
  capabilities: [],

  // Readiness
  ebitdaPositive: "",
  whyJoinNoa: "",
  biggestProjectSize: "",
  projectFinished: "",
  fundingStage: "",
  certifications: [],
};

function Input({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input
        {...props}
        className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
      />
    </div>
  );
}

function Textarea({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <textarea
        {...props}
        className="w-full rounded-xl border px-3 py-2 min-h-[100px] focus:outline-none focus:ring-2 focus:ring-green-500"
      />
    </div>
  );
}

function Select({ label, options, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <select
        {...props}
        className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
      >
        <option value="">Select {label}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

function MultiSelect({ label, options, values, onChange }) {
  const toggle = (value) => {
    if (values.includes(value)) {
      onChange(values.filter((v) => v !== value));
    } else {
      onChange([...values, value]);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      {options.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
          {options.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => toggle(option)}
              className={`text-left rounded-xl border px-3 py-2 transition ${
                values.includes(option)
                  ? "bg-green-50 border-green-600 text-green-700"
                  : "hover:bg-gray-50"
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">Select one or more sectors first to see relevant offerings and needs.</p>
      )}
    </div>
  );
}

function TagInput({ label, values, onChange, placeholder }) {
  const [input, setInput] = useState("");

  const addTag = () => {
    const tag = input.trim();
    if (!tag || values.includes(tag)) return;
    onChange([...values, tag]);
    setInput("");
  };

  const removeTag = (tag) => {
    onChange(values.filter((v) => v !== tag));
  };

  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      <div className="flex gap-2 mb-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addTag();
            }
          }}
          placeholder={placeholder}
          className="flex-1 rounded-xl border px-3 py-2"
        />
        <button
          type="button"
          onClick={addTag}
          className="px-4 py-2 bg-green-600 text-white rounded-xl"
        >
          Add
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {values.map((tag) => (
          <span
            key={tag}
            className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm cursor-pointer"
            onClick={() => removeTag(tag)}
            title="Click to remove"
          >
            {tag} ×
          </span>
        ))}
      </div>
    </div>
  );
}

function getSectorPositionOptions(sectors, position) {
  if (!sectors.length || !position) return [];
  const key = position === "Enabler" ? "enablers" : position.toLowerCase();
  return sectors
    .flatMap((sector) => SECTOR_DETAILS[sector]?.[key] || [])
    .filter((item, index, arr) => item && arr.indexOf(item) === index);
}

export default function OnboardingWizard() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const progress = ((step + 1) / STEPS.length) * 100;

  const update = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "sectors" || field === "valueChainPosition") {
        next.sectorSpecializations = [];
      }
      return next;
    });
  };

  const validateStep = () => {
    const e = {};

    if (step === 0) {
      if (!form.name.trim()) e.name = "Name is required";
      if (!form.email.trim()) e.email = "Email is required";
      if (form.password.length < 8)
        e.password = "Password must be at least 8 characters";
      if (form.password !== form.confirmPassword)
        e.confirmPassword = "Passwords do not match";
    }

    if (step === 1) {
      if (!form.organizationName.trim())
        e.organizationName = "Organization name is required";
      if (!form.country) e.country = "Country is required";
    }

    if (step === 2) {
      if (!form.userType) e.userType = "Value Chain User type is required";
      if (!form.sectors.length) e.sectors = "Select at least one sector";
      if (!form.valueChainPosition)
        e.valueChainPosition = "Value chain position is required";
    }

    if (step === 3) {
      if (!form.offers.length && !form.needs.length) {
        e.offers = "Add at least one offer or need";
      }
    }

    if (step === 4) {
      if (!form.ebitdaPositive)
        e.ebitdaPositive = "Please select EBITDA status";
    
      if (!form.whyJoinNoa)
        e.whyJoinNoa = "Please select why you want to join";
    
      if (!form.biggestProjectSize)
        e.biggestProjectSize = "Project size is required";
    
      if (!form.projectFinished)
        e.projectFinished = "Please select project completion timeline";
    
      if (!form.fundingStage)
        e.fundingStage = "Funding stage is required";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const podEligibilityScore = useMemo(() => {
    let score = 0;
    if (form.organizationName) score += 10;
    if (form.sectors.length) score += 15;
    if (form.valueChainPosition) score += 15;
    if (form.offers.length) score += 15;
    if (form.needs.length) score += 15;
    return Math.min(score, 100);
  }, [form]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await API.post('/auth/register', form);
      await new Promise((resolve) => setTimeout(resolve, 1500));
      alert(
        (response && response.data && response.data.msg) ||
        "Registration completed successfully."
      );
      
      window.location.href = "/login";
      console.log("Submitted form:", form);
    } catch (err) {
      console.error(err);

      const message =
        err?.response?.data?.msg ||
        err?.response?.data?.message ||
        "Registration failed. Please try again.";

      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-4">
            <Input
              label="Full Name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
            />
            {errors.name && <Error text={errors.name} />}

            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
            />
            {errors.email && <Error text={errors.email} />}

            

            <Input
              label="Password"
              type="password"
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
            />
            {errors.password && <Error text={errors.password} />}

            <Input
              label="Confirm Password"
              type="password"
              value={form.confirmPassword}
              onChange={(e) => update("confirmPassword", e.target.value)}
            />
            {errors.confirmPassword && (
              <Error text={errors.confirmPassword} />
            )}
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            <Input
              label="Organization Name"
              value={form.organizationName}
              onChange={(e) => update("organizationName", e.target.value)}
            />
            {errors.organizationName && (
              <Error text={errors.organizationName} />
            )}

            <Input
              label="Job Title"
              value={form.jobTitle}
              onChange={(e) => update("jobTitle", e.target.value)}
            />

            <Input
              label="Website"
              value={form.website}
              onChange={(e) => update("website", e.target.value)}
            />
            
            <Select
              label="Country"
              options={COUNTRIES}
              value={form.country}
              onChange={(e) => update("country", e.target.value)}
            />

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Operation Since"
                options={OPERATION_SINCE}
                value={form.foundedYear}
                onChange={(e) => update("foundedYear", e.target.value)}
              />
              <Select
                label="Organization Size"
                options={ORGANIZATION_SIZES}
                value={form.organizationSize}
                onChange={(e) =>
                  update("organizationSize", e.target.value)
                }
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <Select
              label="Value Chain User Type"
              options={USER_TYPES}
              value={form.userType}
              onChange={(e) => update("userType", e.target.value)}
            />
            {errors.userType && <Error text={errors.userType} />}

            <MultiSelect
              label="Sectors"
              options={SECTORS}
              values={form.sectors}
              onChange={(values) => update("sectors", values)}
            />
            {errors.sectors && <Error text={errors.sectors} />}

            <Select
              label="Value Chain Position"
              options={VALUE_CHAIN_POSITIONS}
              value={form.valueChainPosition}
              onChange={(e) =>
                update("valueChainPosition", e.target.value)
              }
            />
            {errors.valueChainPosition && (
              <Error text={errors.valueChainPosition} />
            )}

            {form.sectors.length > 0 && form.valueChainPosition && (
              <div className="space-y-4 mt-6">
                <MultiSelect
                  label={`${form.valueChainPosition} focus areas`}
                  options={getSectorPositionOptions(
                    form.sectors,
                    form.valueChainPosition
                  )}
                  values={form.sectorSpecializations}
                  onChange={(values) =>
                    update("sectorSpecializations", values)
                  }
                />
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <MultiSelect
              label="Offerings (What members may offer)"
              options={getPodOptionsFromSectors(form.sectors, "offerings")}
              values={form.offers}
              onChange={(values) => update("offers", values)}
            />

            <MultiSelect
              label="Needs / Asks"
              options={getPodOptionsFromSectors(form.sectors, "needs")}
              values={form.needs}
              onChange={(values) => update("needs", values)}
            />

            <TagInput
              label="Differentiator"
              values={form.capabilities}
              onChange={(values) => update("capabilities", values)}
              placeholder="e.g. AI Optimization"
            />

            {errors.offers && <Error text={errors.offers} />}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <Select
              label="EBITDA Positive?"
              options={EBITDA_OPTIONS}
              value={form.ebitdaPositive}
              onChange={(e) => update("ebitdaPositive", e.target.value)}
            />

            {errors.ebitdaPositive && <Error text={errors.ebitdaPositive} />}


            <Select
              label="Why join NOA Sustainability Lab?"
              options={NOA_JOIN_REASONS}
              value={form.whyJoinNoa}
              onChange={(e) => update("whyJoinNoa", e.target.value)}
            />

            {errors.whyJoinNoa && <Error text={errors.whyJoinNoa} />}


            <Select
              label="Biggest project size in $"
              options={PROJECT_SIZE_OPTIONS}
              value={form.biggestProjectSize}
              onChange={(e) => update("biggestProjectSize", e.target.value)}
            />

            {errors.biggestProjectSize && <Error text={errors.biggestProjectSize} />}


            <Select
              label="When did you finish the above project"
              options={PROJECT_FINISHED_OPTIONS}
              value={form.projectFinished}
              onChange={(e) => update("projectFinished", e.target.value)}
            />

            {errors.projectFinished && <Error text={errors.projectFinished} />}


            <Select
              label="What is the source of funding of your project?"
              options={FUNDING_STAGES}
              value={form.fundingStage}
              onChange={(e) => update("fundingStage", e.target.value)}
            />

            {errors.fundingStage && <Error text={errors.fundingStage} />}

            <TagInput
              label="Certifications"
              values={form.certifications}
              onChange={(values) => update("certifications", values)}
              placeholder="e.g. ISO 9001"
            />
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="rounded-2xl bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-green-800">
                    AI Pod Profile Completion Score
                  </h3>
                  <p className="text-sm text-green-700 mt-1">
                    Based on your profile completeness and strategic fit.
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-green-700">
                    {podEligibilityScore}%
                  </div>
                  <div className="text-sm text-green-600 font-medium">
                    {podEligibilityScore >= 90
                      ? "Pod Ready"
                      : podEligibilityScore >= 70
                      ? "Strong Profile"
                      : podEligibilityScore >= 40
                      ? "Good Start"
                      : "Needs Improvement"}
                  </div>
                </div>
              </div>
            </div>

            <ReviewSection
              title="Personal Information"
              items={[
                ["Full Name", form.name],
                ["Email", form.email],
                ["Job Title", form.jobTitle || "—"],
              ]}
            />

            <ReviewSection
              title="Organization Information"
              items={[
                ["Organization", form.organizationName],
                ["Website", form.website || "—"],
                ["Country", form.country],
                ["Founded Year", form.foundedYear || "—"],
                ["Organization Size", form.organizationSize || "—"],
              ]}
            />

            <ReviewSection
              title="Sector Profile"
              items={[
                ["User Type", form.userType],
                ["Sectors", form.sectors.join(", ") || "—"],
                ["Value Chain Position", form.valueChainPosition],
                [
                  "Sector Focus Areas",
                  form.sectorSpecializations.length
                    ? form.sectorSpecializations.join(", ")
                    : "—",
                ],
              ]}
            />

            <ReviewSection
              title="Offers, Needs & Capabilities"
              items={[
                ["Offers", form.offers.length ? form.offers.join(", ") : "—"],
                ["Needs", form.needs.length ? form.needs.join(", ") : "—"],
                [
                  "Capabilities",
                  form.capabilities.length
                    ? form.capabilities.join(", ")
                    : "—",
                ],
              ]}
            />

            <ReviewSection
              title="Past Project Execution Credentials"
              items={[
                ["EBITDA Positive?", form.ebitdaPositive || "—"],
                ["Why join NOA Sustainability Lab?", form.whyJoinNoa || "—"],
                ["Biggest project size", form.biggestProjectSize || "—"],
                ["When did you finish the above project", form.projectFinished || "—"],
                ["Funding Stage", form.fundingStage || "—"],
                [
                  "Certifications",
                  form.certifications.length
                    ? form.certifications.join(", ")
                    : "—",
                ],
              ]}
            />

            <div className="rounded-2xl border bg-blue-50 p-4">
              <h4 className="font-semibold text-blue-900 mb-2">
                What happens after registration?
              </h4>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>Your profile will be analyzed by AI.</li>
                <li>Capability tags and summaries will be generated.</li>
                <li>High-potential matches will be identified.</li>
                <li>Potential Value Pods will be recommended.</li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-3xl overflow-hidden">
        <div className="p-8 border-b">
          <h1 className="text-3xl font-bold">NOA Value Pod Platform</h1>
          <p className="text-gray-600 mt-1">
            Multi-Step Onboarding Wizard
          </p>

          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span>
                Step {step + 1} of {STEPS.length}: {STEPS[step]}
              </span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-6 gap-2 mt-6">
            {STEPS.map((label, index) => (
              <div
                key={label}
                className={`text-xs text-center p-2 rounded-xl ${
                  index === step
                    ? "bg-green-100 text-green-700 font-semibold"
                    : index < step
                    ? "bg-gray-100"
                    : "text-gray-400"
                }`}
              >
                {index < step ? <Check className="w-4 h-4 mx-auto" /> : label}
              </div>
            ))}
          </div>
        </div>

        <div className="p-8">{renderStep()}</div>

        <div className="px-8 pb-8 flex justify-between">
          <button
            type="button"
            onClick={back}
            disabled={step === 0}
            className="px-5 py-2.5 rounded-xl border disabled:opacity-50 flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>

          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={next}
              className="px-5 py-2.5 rounded-xl bg-green-600 text-white flex items-center gap-2"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2.5 rounded-xl bg-green-600 text-white disabled:opacity-50"
            >
              {loading ? "Submitting..." : "Complete Registration"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Error({ text }) {
  return <p className="text-sm text-red-600 mt-1">{text}</p>;
}

function RangeInput({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      <input type="range" className="w-full" {...props} />
    </div>
  );
}

function ReviewSection({ title, items }) {
  return (
    <div className="rounded-2xl border bg-white p-5">
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      <div className="space-y-3">
        {items.map(([label, value]) => (
          <div
            key={label}
            className="grid grid-cols-1 md:grid-cols-3 gap-2 border-b pb-2 last:border-b-0 last:pb-0"
          >
            <div className="text-sm font-medium text-gray-600">{label}</div>
            <div className="md:col-span-2 text-sm text-gray-900 break-words">
              {value || "—"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
